namespace Solovis.Reports.Parameters.Stats {
  // Common help text configurations
  export const HELP_CONFIGS = {
    PERCENT_OF_ALL: { help: PercentOfAllHelpText },
    LOCAL_VALUES: { help: LocalValuesHelpText },
    NET_VALUE_GAIN_LOSS: { help: NetValueGainLossHelpText },
    TWR_VARIATION: {
      help: "Applies the performance variation of the cumulative/annualized TWR as defined in Site Settings.",
    },
    IRR_VARIATION: {
      help: "Applies the performance variation of the cumulative/annualized IRR as defined in Site Settings.",
    },
  };

  export interface StatisticsOptions {
    values?: StatisticsValuesOptions;
    performance?: StatisticsOptionsPerformance;
    transactions?: StatSetOptionsTx;
    basicStats?: StatisticOptionsBasicStats;
    basicStatsBenchmark?: StatisticsBenchmarkBasicStats;
    betaAlpha?: StatisticsBetaAlpha;
    otherRatios?: StatisticsOtherRatios;
    peaksAndTroughs?: StatisticsPeaksAndTroughs;
    publicMarketEquivalent?: StatisticsPublicMarketEquivalent;
  }

  export interface StatisticsValuesOptions {
    startValue?: boolean;
    endValue?: boolean;
    startPrice?: boolean;
    startUnits?: boolean;
    showLocal?: boolean;
    valueType?: boolean;
    endUnits?: boolean;
    endUnitPrice?: boolean;
    percentOfAll?: boolean;
  }

  export interface StatisticsOptionsPerformance {
    twr?: boolean;
    twrMethod?: string;
    irr?: boolean;
    localTwr?: boolean;
    irrMethod?: string;
    netValueGainLoss?: boolean;
    localNetValueGainLoss?: boolean;
  }

  export interface StatisticOptionsBasicStats {
    standardDeviation?: boolean;
    annualizedStandardDeviation?: boolean;
    variance?: boolean;
    downsideDeviation?: boolean;
    arithmeticMean?: boolean;
    sampleSkewness?: boolean;
    sampleExcessKurtosis?: boolean;
    gainLossSummary?: boolean;
  }

  export interface StatisticsBenchmarkBasicStats {
    basicStatsBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
    correlation?: boolean;
    covariance?: boolean;
    deltaMean?: boolean;
    deltaStandardDeviation?: boolean;
    downsideDeltaStandardDeviation?: boolean;
  }

  export interface StatisticsBetaAlpha {
    betaAlpha?: boolean;
    betaAlphaMarketBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
    annualizedAlpha?: boolean;
    annualizedTreynorRatio?: boolean;
  }

  export interface StatisticsOtherRatios {
    sortinoRatio?: boolean;
    sortinoRatioBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
    sharpeRatio?: boolean;
    sharpeRatioBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
    annualizedSharpeRatio?: boolean;
    upsideDownsideCaptureRatio?: boolean;
    upsideDownsideCaptureRatioBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
  }

  export interface StatisticsPeaksAndTroughs {
    maxDrawdown?: boolean;
    sincePeakPerformance?: boolean;
    useQuarterEndsForPeaksAndTroughs?: boolean;
  }

  export interface StatisticsPublicMarketEquivalent {
    pme?: boolean;
    pmeBenchmark?: Solovis.Reports.Parameters.Stats.BenchmarkOption;
    longNickelsPme?: boolean;
    pmePlus?: boolean;
    modifiedPme?: boolean;
    kaplanSchoarPme?: boolean;
    directAlpha?: boolean;
    excessIrr?: boolean;
  }

  export class StatisticsParameterControl extends Solovis.UI.BaseControl {
    private _options: StatisticsOptions;
    private _sectionsInUse: any[];

    constructor(options) {
      super(options);
      this._options =
        typeof options.initialValue !== "undefined"
          ? options.initialValue
          : null;
      this._sectionsInUse = options.settings.sectionsInUse || [];
    }

    public async doRender() {
      let self = this;
      let label = $("<a>").text("Statistics").appendTo(this.container);
      label.click(function () {
        self._showEditor();
      });
      let editLink = $("<a>")
        .appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () {
        self._showEditor();
      });
    }

    private _showEditor() {
      const editorDialog = new StatisticsEditorDialog({
        sections: this._sectionsInUse,
        options: this._options,
        saveCallback: (data: StatisticsOptions) => this.setParameterValue(data),
      });
      editorDialog.show();
    }

    public _setCurrentSourceDataOptions(data) {
      this._options = data;
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this,
        StatisticsParameterControl,
        this._setCurrentSourceDataOptions,
        newValue,
        more
      );
    }
    public getParameterValue() {
      return Solovis.deepCopy(this._options);
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }
  }

  export class StatisticsEditorDialog {
    private _opts: StatisticsOptions;
    private _sections: string[];
    private _saveCallback;
    private formBuilder: Solovis.UI.InputControls.FormBuilder;

    private twrVariationsOptionsList: any[] = new Array();
    private irrVariationsOptionsList: any[] = new Array();
    private defaultBenchmarkOptionsList: { value: string, text: string }[] = [
      { text: "None", value: "None" },
      { text: "Default", value: "Default" },
      { text: "Custom", value: "Custom" },
    ];

    public constructor(options: {
      sections: string[];
      options: StatisticsOptions;
      saveCallback: any;
    }) {
      this._opts = options.options ? options.options : {};
      this._sections = options.sections ? options.sections : [];
      this._saveCallback = options.saveCallback;
    }

    private _addValues(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "values", label: "Values" },
          {
            name: "values.startValue",
            label: "Start Value",
            initialValue: options.values?.startValue ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.endValue",
            label: "End Value",
            initialValue: options.values?.endValue ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.startPrice",
            label: "Start Price",
            initialValue: options.values?.startPrice ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.startUnits",
            label: "Start Units",
            initialValue: options.values?.startUnits ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.endUnits",
            label: "End Units",
            initialValue: options.values?.endUnits ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.endUnitPrice",
            label: "End Unit Price",
            initialValue: options.values?.endUnitPrice ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.percentOfAll",
            label: "% Of All Value",
            help: PercentOfAllHelpText,
            initialValue: options.values?.percentOfAll ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.showLocal",
            label: "Show Local",
            help: LocalValuesHelpText,
            initialValue: options.values?.showLocal ?? false,
            editor: { type: "bool" },
          },
          {
            name: "values.valueType",
            label: "Value Type",
            initialValue: options.values?.valueType ?? false,
            editor: { type: "bool" },
            customProperty: "example",
          } as any,
        ]);
      }
    }

    private _addPerformance(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "performance", label: "Performance" },
          {
            name: "performance.twr",
            label: "TWR",
            initialValue: options.performance?.twr ?? false,
            editor: { type: "bool" },
          },
          {
            name: "performance.twrMethod",
            label: "TWR Method",
            help: "Applies the performance variation of the cumulative/annualized TWR as defined in Site Settings.",
            initialValue: options.performance?.twrMethod,
            editor: {
              type: "select",
              selectOptions: {
                data: this.twrVariationsOptionsList,
                showClearButton: true,
              },
            },
            isVisible: (entry) => !_.isEmpty(this.twrVariationsOptionsList),
          },
          {
            name: "performance.localTwr",
            label: "Local TWR",
            initialValue: options.performance?.localTwr ?? false,
            editor: { type: "bool" },
          },
          {
            name: "performance.irr",
            label: "IRR",
            initialValue: options.performance?.irr ?? false,
            editor: { type: "bool" },
          },
          {
            name: "performance.irrMethod",
            label: "IRR Method",
            help: "Applies the performance variation of the cumulative/annualized IRR as defined in Site Settings.",
            initialValue: options.performance?.irrMethod,
            editor: {
              type: "select",
              selectOptions: {
                data: this.irrVariationsOptionsList,
                showClearButton: true,
              },
            },
            isVisible: (entry) => !_.isEmpty(this.irrVariationsOptionsList),
          },
          {
            name: "performance.netValueGainLoss",
            label: "Net Value Gain/Loss",
            help: NetValueGainLossHelpText,
            initialValue: options.performance?.netValueGainLoss ?? false,
            editor: { type: "bool" },
          },
          {
            name: "performance.localNetValueGainLoss",
            label: "Local Net Value Gain/Loss",
            help: NetValueGainLossHelpText + " In local currency.",
            initialValue: options.performance?.localNetValueGainLoss ?? false,
            editor: { type: "bool" },
          },
        ]);
      }
    }

    private _addTx(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "transactions", label: "Transactions" },
          {
            name: "transactions.netAmountIn",
            label: "Net Amount In",
            initialValue: options.transactions?.netAmountIn ?? false,
            editor: { type: "bool" },
          },
          {
            name: "transactions.amountIn",
            label: "Amount In",
            initialValue: options.transactions?.amountIn ?? false,
            editor: { type: "bool" },
          },
          {
            name: "transactions.amountOut",
            label: "Amount Out",
            initialValue: options.transactions?.amountOut ?? false,
            editor: { type: "bool" },
          },
          {
            name: "transactions.dietzWeightedIn",
            label: "Dietz Weighted In",
            initialValue: options.transactions?.dietzWeightedIn ?? false,
            editor: { type: "bool" },
          },
        ]);
      }
    }

    private _addBasicStats(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "basicStats", label: "Basic Stats" },
          {
            name: "basicStats.standardDeviation",
            label: "Standard Deviation",
            initialValue: options.basicStats?.standardDeviation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.standardDeviation,
          },
          {
            name: "basicStats.annualizedStandardDeviation",
            label: "Annualized Standard Deviation",
            initialValue: options.basicStats?.annualizedStandardDeviation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.annualizedStandardDeviation,
          },
          {
            name: "basicStats.variance",
            label: "Variance",
            initialValue: options.basicStats?.variance ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.variance,
          },
          {
            name: "basicStats.downsideDeviation",
            label: "Downside Standard Deviation",
            initialValue: options.basicStats?.downsideDeviation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.downsideStandardDeviation,
          },
          {
            name: "basicStats.arithmeticMean",
            label: "Arithmetic Mean",
            initialValue: options.basicStats?.arithmeticMean ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.mean,
          },
          {
            name: "basicStats.sampleSkewness",
            label: "Sample Skewness",
            initialValue: options.basicStats?.sampleSkewness ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.sampleSkewness,
          },
          {
            name: "basicStats.sampleExcessKurtosis",
            label: "Sample Excess Kurtosis",
            initialValue: options.basicStats?.sampleExcessKurtosis ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.sampleExcessKurtosis,
          },
          {
            name: "basicStats.gainLossSummary",
            label: "Gain/Loss Summary",
            initialValue: options.basicStats?.gainLossSummary ?? false,
            editor: { type: "bool" },
            help: Solovis.Reports.Parameters.Stats.NetValueGainLossHelpText,
          },
        ]);
      }
    }

    private _addBenchmarkBasicStats(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        const bmOptions = [
          { text: "None", value: "None" },
          { text: "Primary", value: "Primary" },
          { text: "Secondary", value: "Secondary" },
          { text: "Custom", value: "Custom" },
        ];
        const basicStatsBmCmpts = this.getBenchmarkCmpts(
          "basicStatsBenchmark.basicStatsBenchmark",
          "Basic Stats Benchmark",
          () => true,
          options.basicStatsBenchmark?.basicStatsBenchmark,
          bmOptions);
        this.formBuilder.addEntries([
          {
            isGroupHeader: true,
            name: "basicStatsBenchmark",
            label: "Benchmark Basic Stats",
          },
          basicStatsBmCmpts[0],
          basicStatsBmCmpts[1],
          {
            name: "basicStatsBenchmark.correlation",
            label: "Correlation",
            initialValue: options.basicStatsBenchmark?.correlation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.correlation,
          },
          {
            name: "basicStatsBenchmark.covariance",
            label: "Covariance",
            initialValue: options.basicStatsBenchmark?.covariance ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.covariance,
          },
          {
            name: "basicStatsBenchmark.deltaMean",
            label: "Delta Mean",
            initialValue: options.basicStatsBenchmark?.deltaMean ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.deltaMean,
          },
          {
            name: "basicStatsBenchmark.deltaStandardDeviation",
            label: "Delta Standard Deviation",
            initialValue: options.basicStatsBenchmark?.deltaStandardDeviation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.deltaStandardDeviation,
          },
          {
            name: "basicStatsBenchmark.downsideDeltaStandardDeviation",
            label: "Downside Delta Standard Deviation",
            initialValue: options.basicStatsBenchmark?.downsideDeltaStandardDeviation ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._statsFieldHelp.downsideDeltaStandardDeviation,
          },
        ]);
      }
    }

    private _addBetaAlpha(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        const betaAlphaBmCmpts = this.getBenchmarkCmpts(
          "betaAlpha.betaAlphaMarketBenchmark",
          "Market Benchmark",
          () => this.formBuilder.getValueByName("betaAlpha.betaAlpha") === true,
          options.betaAlpha?.betaAlphaMarketBenchmark,
          this.defaultBenchmarkOptionsList);
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "betaAlpha", label: "Beta/Alpha" },
          {
            name: "betaAlpha.betaAlpha",
            label: "Beta/Alpha",
            initialValue: options.betaAlpha?.betaAlpha ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._betaAlphaFieldHelp.betaAlpha,
          },
          betaAlphaBmCmpts[0],
          betaAlphaBmCmpts[1],
          {
            name: "betaAlpha.annualizedAlpha",
            label: "Annualized Alpha",
            initialValue: options.betaAlpha?.annualizedAlpha ?? false,
            editor: { type: "bool" },
            isVisible: (entry) => {
              return (
                this.formBuilder.getValueByName("betaAlpha.betaAlpha") === true
              );
            },
            help: StatSetEditorDialog._betaAlphaFieldHelp.annualizedAlpha,
          },
          {
            name: "betaAlpha.annualizedTreynorRatio",
            label: "Annualized Treynor Ratio",
            initialValue: options.betaAlpha?.annualizedTreynorRatio ?? false,
            editor: { type: "bool" },
            isVisible: (entry) => {
              return (
                this.formBuilder.getValueByName("betaAlpha.betaAlpha") === true
              );
            },
            help: StatSetEditorDialog._betaAlphaFieldHelp.annualizedTreynorRatio,
          },
        ]);
      }
    }

    private _addOtherRatios(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        const sortinoBmCmpts = this.getBenchmarkCmpts(
          "otherRatios.sortinoRatioBenchmark",
          "Sortino Ratio Benchmark",
          () => this.formBuilder.getValueByName("otherRatios.sortinoRatio") === true,
          options.otherRatios?.sortinoRatioBenchmark,
          this.defaultBenchmarkOptionsList);
        const sharpeBmCmpts = this.getBenchmarkCmpts(
          "otherRatios.sharpeRatioBenchmark",
          "Sharpe Ratio Benchmark",
          () => this.formBuilder.getValueByName("otherRatios.sharpeRatio") === true,
          options.otherRatios?.sharpeRatioBenchmark,
          this.defaultBenchmarkOptionsList);
        const upsideDownsideBmCmpts = this.getBenchmarkCmpts(
          "otherRatios.upsideDownsideCaptureRatioBenchmark",
          "Upside/Downside Capture Ratio Benchmark",
          () => this.formBuilder.getValueByName("otherRatios.upsideDownsideCaptureRatio") === true,
          options.otherRatios?.upsideDownsideCaptureRatioBenchmark,
          this.defaultBenchmarkOptionsList);
        this.formBuilder.addEntries([
          { isGroupHeader: true, name: "otherRatios", label: "Other Ratios" },
          {
            name: "otherRatios.sortinoRatio",
            label: "Sortino Ratio",
            initialValue: options.otherRatios?.sortinoRatio ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._ratiosFieldHelp.sortinoRatio,
          },
          sortinoBmCmpts[0],
          sortinoBmCmpts[1],
          {
            name: "otherRatios.sharpeRatio",
            label: "Sharpe Ratio",
            initialValue: options.otherRatios?.sharpeRatio ?? false,
            help: StatSetEditorDialog._ratiosFieldHelp.sharpeRatio,
            editor: { type: "bool" },
          },
          {
            name: "otherRatios.annualizedSharpeRatio",
            label: "Annualized Sharpe Ratio",
            initialValue: options.otherRatios?.annualizedSharpeRatio ?? false,
            help: StatSetEditorDialog._ratiosFieldHelp.annualizedSharpeRatio,
            editor: { type: "bool" },
          },
          sharpeBmCmpts[0],
          sharpeBmCmpts[1],
          {
            name: "otherRatios.upsideDownsideCaptureRatio",
            label: "Upside/Downside Capture Ratio",
            initialValue: options.otherRatios?.upsideDownsideCaptureRatio ?? false,
            help: StatSetEditorDialog._ratiosFieldHelp.upsideDownsideCaptureRatio,
            editor: { type: "bool" },
          },
          upsideDownsideBmCmpts[0],
          upsideDownsideBmCmpts[1],
        ]);
      }
    }

    private _addPeaksAndTroughs(renderSection: boolean) {
      let options = this._opts;
      if (renderSection) {
        // IStats StatSetParamater control names these options without [sectionName].[]
        this.formBuilder.addEntries([
          {
            isGroupHeader: true,
            name: "peaksAndTroughs",
            label: "Peaks And Troughs",
          },
          {
            name: "peaksAndTroughs.maxDrawdown",
            label: "Max Drawdown",
            help: {
              url: "~/Help/InvestmentModelHelp/CalculationsReference#max_drawdown",
            },
            initialValue: !!options.peaksAndTroughs?.maxDrawdown,
            editor: {
              type: "bool",
            },
          },
          {
            name: "peaksAndTroughs.sincePeakPerformance",
            label: "Since Peak Performance",
            help: {
              url: "~/Help/InvestmentModelHelp/CalculationsReference#since_peak_performance",
            },
            initialValue: !!options.peaksAndTroughs?.sincePeakPerformance,
            editor: {
              type: "bool",
            },
          },
          {
            name: "peaksAndTroughs.useQuarterEndsForPeaksAndTroughs",
            label: "Use Quarter Ends For Peaks And Troughs",
            help: "Only consider quarter end dates as possible peaks or troughs.",
            initialValue: !!options.peaksAndTroughs?.useQuarterEndsForPeaksAndTroughs,
            editor: {
              type: "bool",
            },
          },
        ]);
      }
    }

    private _addPublicMarketEquivalent(renderSection: boolean) {
      const options = this._opts;
      if (renderSection) {
        const pmeBmCmpts = this.getBenchmarkCmpts(
          "publicMarketEquivalent.pmeBenchmark",
          "PME Benchmark",
          () => this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true,
          options.publicMarketEquivalent?.pmeBenchmark,
          this.defaultBenchmarkOptionsList);
        this.formBuilder.addEntries([
          {
            isGroupHeader: true,
            name: "publicMarketEquivalent",
            label: "Public Market Equivalent",
          },
          {
            name: "publicMarketEquivalent.pme",
            label: "PME",
            initialValue: options.publicMarketEquivalent?.pme ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.pme,
          },
          pmeBmCmpts[0],
          pmeBmCmpts[1],
          {
            name: "publicMarketEquivalent.longNickelsPme",
            label: "Long-Nickels PME",
            initialValue: options.publicMarketEquivalent?.longNickelsPme ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.longNickelsPme,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
          {
            name: "publicMarketEquivalent.pmePlus",
            label: "PME+",
            initialValue: options.publicMarketEquivalent?.pmePlus ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.pmePlus,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
          {
            name: "publicMarketEquivalent.modifiedPme",
            label: "Modified PME",
            initialValue: options.publicMarketEquivalent?.modifiedPme ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.modifiedPme,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
          {
            name: "publicMarketEquivalent.kaplanSchoarPme",
            label: "Kaplan-Schoar PME",
            initialValue: options.publicMarketEquivalent?.kaplanSchoarPme ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.kaplanSchoarPme,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
          {
            name: "publicMarketEquivalent.directAlpha",
            label: "Direct Alpha",
            initialValue: options.publicMarketEquivalent?.directAlpha ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.directAlpha,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
          {
            name: "publicMarketEquivalent.excessIrr",
            label: "Excess IRR",
            initialValue: options.publicMarketEquivalent?.excessIrr ?? false,
            editor: { type: "bool" },
            help: StatSetEditorDialog._pmeFieldHelp.excessIrr,
            isVisible: (entry) => {
              return this.formBuilder.getValueByName("publicMarketEquivalent.pme") === true;
            },
          },
        ]);
      }
    }

    private getBenchmarkCmpts(
      name: string,
      label: string,
      isVisible: () => boolean,
      value: Solovis.Reports.Parameters.Stats.BenchmarkOption,
      selectOptionsData: { value: string, text: string }[]): any[] {

      const firstCmptName = name + ".source";
      return [{
        name: firstCmptName,
        label: label,
        initialValue: value?.source ?? "Default",
        isVisible: isVisible,
        editor: {
          type: "select",
          selectOptions: {
            data: selectOptionsData,
          },
        },
      },
      {
        name: name + ".customBenchmark",
        label: "",
        initialValue: value?.customBenchmark,
        editor: {
          type: "parameter",
          parameterOptions: {
            parameterType: "entityOrInvestment"
          }
        },
        isVisible: (formEntry) => {
          let mode = formEntry.formBuilder.getValueByName(firstCmptName);
          return mode === "Custom";
        }
      }];
    }

    private _getNewStatisticsOptions(
      entries: UI.InputControls.FormEntry[]
    ): StatisticsOptions {
      const newOptions: StatisticsOptions = {};

      // Process each entry that has a dot notation name (excludes group headers)
      entries.forEach((entry) => {
        // Skip group headers and entries without dot notation
        if (entry.isGroupHeader() || !entry.name.includes(".")) {
          return;
        }

        // Check visibility condition if it exists
        if (entry._entryOptions.isVisible) {
          try {
            const isVisible = entry._entryOptions.isVisible(entry);
            if (!isVisible) {
              return; // Skip invisible entries
            }
          } catch (ex) {
            console.log(
              `ERROR: _getNewStatisticsOptions visibility check for ${entry.name}:`,
              ex
            );
            return;
          }
        }

        // Parse the JSON notation to extract section name and property property name
        const [sectionName, propertyName, nestedProperty] = entry.name.split(".");

        // Initialize section if it doesn't exist
        if (!newOptions[sectionName]) {
          newOptions[sectionName] = {};
        }

        // Get the value and assign it
        try {
          if (nestedProperty) {
            if (!newOptions[sectionName][propertyName]) {
              newOptions[sectionName][propertyName] = {};
            }

            newOptions[sectionName][propertyName][nestedProperty] = this.formBuilder.getValueByName(entry.name);
          }
          else {
            newOptions[sectionName][propertyName] = this.formBuilder.getValueByName(entry.name);
          }

        }
        catch (ex) {
          console.log(`ERROR: _getNewStatisticsOptions getting value for ${entry.name}:`, ex);
        }
      });

      return newOptions;
    }

    private shouldRenderSection(section: string): boolean {
      return this._sections.length === 0 || this._sections.includes(section);
    }

    public show() {
      this.formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 700,
        labelColumnWidth: 220,
      });

      let customEnvironmentUserData = this.formBuilder.portalServices().customEnvironmentUserData();
      if (customEnvironmentUserData?.ModelSettings) {
        let modelSettings = customEnvironmentUserData.ModelSettings;
        if (modelSettings?.performanceVariationsConfig?.variations) {
          for (let variation of modelSettings.performanceVariationsConfig.variations) {
            if (variation.allowForTwr) {
              this.twrVariationsOptionsList.push({
                text: variation.label,
                value: variation.code,
              });
            }
            if (variation.allowForIrr) {
              this.irrVariationsOptionsList.push({
                text: variation.label,
                value: variation.code,
              });
            }
          }
        }
      }

      this._addValues(this.shouldRenderSection("values"));
      this._addPerformance(this.shouldRenderSection("performance"));
      this._addTx(this.shouldRenderSection("transactions"));
      this._addBasicStats(this.shouldRenderSection("basicStats"));
      this._addBenchmarkBasicStats(this.shouldRenderSection("basicStatsBenchmark"));
      this._addBetaAlpha(this.shouldRenderSection("betaAlpha"));
      this._addOtherRatios(this.shouldRenderSection("otherRatios"));
      this._addPeaksAndTroughs(this.shouldRenderSection("peaksAndTroughs"));
      this._addPublicMarketEquivalent(this.shouldRenderSection("publicMarketEquivalent"));

      this.formBuilder.setupDone();
      this.formBuilder.showAsDialog({
        title: "Edit Statistics Set",
        desiredWidth: 720,
        desiredHeight: 800,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          let newOptions = this._getNewStatisticsOptions(
            this.formBuilder.getEntryList()
          );
          this._saveCallback(newOptions);
          return true;
        },
      });
    }
  }
}
